import httpClient from '@/lib/httpClient'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  billingSubscriptionQueryKey,
  type BillingInterval,
} from './useGetBillingSubscription'

type TeamId = string | number | null | undefined

const teamHeaders = (teamId: TeamId) =>
  teamId ? { 'X-Team': String(teamId) } : undefined

/**
 * Create a Stripe Checkout Session on the backend and return its URL.
 * The frontend only redirects the browser — it never handles Stripe ids/keys.
 */
export function useCreateCheckoutSession() {
  return useMutation({
    mutationFn: async ({
      teamId,
      planKey,
      interval = 'month',
    }: {
      teamId?: TeamId
      planKey: string
      interval?: BillingInterval
    }) => {
      const response = await httpClient.post(
        'billing/checkout-session',
        { plan_key: planKey, interval },
        { headers: teamHeaders(teamId) },
      )
      return response.data as { url: string }
    },
  })
}

export type PortalAction = 'update' | 'cancel' | 'payment_method'

/**
 * Stripe Billing Portal session, deep-linked to one flow. Passing planKey +
 * interval with 'update' skips straight to the confirm step for that plan.
 * Caller redirects to the returned URL.
 */
export function useCreatePortalSession() {
  return useMutation({
    mutationFn: async ({
      teamId,
      action,
      planKey,
      interval,
    }: {
      teamId?: TeamId
      action: PortalAction
      planKey?: string
      interval?: BillingInterval
    }) => {
      const response = await httpClient.post(
        'billing/portal-session',
        planKey ? { action, plan_key: planKey, interval } : { action },
        { headers: teamHeaders(teamId) },
      )
      return response.data as { url: string }
    },
  })
}

/** Undo a scheduled cancellation. */
export function useResumeSubscription() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (teamId: TeamId) => {
      await httpClient.post(
        'billing/resume',
        {},
        { headers: teamHeaders(teamId) },
      )
    },
    onSuccess: (_data, teamId) => {
      queryClient.invalidateQueries({
        queryKey: billingSubscriptionQueryKey(teamId),
      })
    },
  })
}
